import { useEffect, useState } from "react";
import {
  OrderItemInput,
  OrderItemStatus,
  OrderItem as OrderItemType,
} from "../../Generated/graphql";
import { Checkbox } from "../Checkbox/Checkbox";
import { Icon } from "../Icon/Icon";
import { OrderItem } from "../OrderItem/OrderItem";
import "./OrderDetails.css";

interface IOrderDetailsProps {
  showMe: boolean;
  orderLines: OrderItemType[];
  openScan: () => void;
  windowWidth: number;
  changeItemAmount: (productNumber: string, amount: number) => void;
  addItem: (items: OrderItemInput) => void;
  addingToStock: boolean;
  removeItems: (productNumbers: string[]) => void;
  selectItems: (productNumbers: string[]) => void;
  selectedItems: string[];
  triggerUpdateOrderItems: () => void;
  changeExcludedState: (productNumber: string, excluded: boolean) => void;
  getItemDisplayState?: (item: OrderItemType) => { status: OrderItemStatus; excluded: boolean; loading: boolean; hasZeroAmount?: boolean };
  handleItemAction?: (itemId: string, included: boolean) => void;
}

export const OrderDetails = ({
  showMe,
  orderLines,
  openScan,
  windowWidth,
  changeItemAmount,
  addItem,
  addingToStock,
  removeItems,
  selectItems,
  selectedItems,
  triggerUpdateOrderItems,
  changeExcludedState,
  getItemDisplayState,
  handleItemAction,
}: IOrderDetailsProps) => {
  const changeAllItems = () => {
    if (selectedItems.length > 0) {
      removeItems(selectedItems);
    } else {
      selectItems(
        orderLines.reduce<string[]>((acc, line) => {
          if (
            line.status === OrderItemStatus.Backorder ||
            line.status === OrderItemStatus.Completed
          ) {
            return acc;
          }
          return [...acc, line.productNumber];
        }, [])
      );
    }
  };

  // whilst the component is mounted, it must keep the ordering of the items, even if they change state
  const [stateOrderlines, setStateOrderlines] = useState<OrderItemType[]>(
    orderLines
  );

  useEffect(() => {
    // Update the state orderlines when props change,
    // this way we have a local representation in the current order,
    // but the props can be in whatever order we want
    const updatedLines = stateOrderlines.map((line) => {
      const newLine = orderLines.find((ol) => ol.id === line.id);
      if (newLine) {
        return newLine;
      }
      return line;
    });

    setStateOrderlines(updatedLines);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderLines]);

  return (
    <div className="OrderDetails" style={{ position: 'relative' }}>
        {addingToStock ?
        <div className="OrderDetails__addToInventory">
        <div className="OrderDetails__scanIn" onClick={() => openScan()}>
          <div className="OrderDetails__icon">
            <Icon name="scanActive" width={15} />
          </div>
          <div className="OrderDetails__scan-text">Scan varer</div>
        </div>
        <div
          className="OrderDetails__addToInventory-button OrderDetails__addToInventory-button--loading"                
        >
          <Icon name="buttonSpinner" width={12} pathStyle={{ fill: '#4092d7' }} />
          <div className="OrderDetails__addToInventory-text">
            Tilføjer...
          </div>
        </div>
        <div className="OrderDetails__addToInventory-checkbox">
          <Checkbox
            checked={selectedItems.length === orderLines.length}
            partiallyChecked={selectedItems.length > 0}
            change={changeAllItems}
          />
        </div>
      </div>
        :
        <div className="OrderDetails__addToInventory">
          <div className="OrderDetails__scanIn" onClick={() => openScan()}>
            <div className="OrderDetails__icon">
              <Icon name="scanActive" width={15} />
            </div>
            <div className="OrderDetails__scan-text">Scan varer</div>
          </div>
          <div
            className="OrderDetails__addToInventory-button"
            onClick={triggerUpdateOrderItems}          
          >
            <Icon name="addBlue" width={12} />
            <div className="OrderDetails__addToInventory-text">
              Godkend valgte
            </div>
          </div>
          <div className="OrderDetails__addToInventory-checkbox">
            <Checkbox
              checked={selectedItems.length === orderLines.length}
              partiallyChecked={selectedItems.length > 0}
              change={changeAllItems}
            />
          </div>
        </div>
        }
     
      <div className="OrderDetails__items">
        {addingToStock && (
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(248, 249, 250, 0.95)',
            backdropFilter: 'blur(2px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '20px 32px',
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              minWidth: '200px'
            }}>
              <Icon name="buttonSpinner" width={24} pathStyle={{ fill: '#4092d7' }} />
              <span style={{
                fontSize: '16px',
                fontWeight: '500',
                color: '#374151',
                letterSpacing: '-0.01em'
              }}>Opdaterer ordre...</span>
            </div>
          </div>
        )}
        {stateOrderlines.map((item, index) => {
          const displayState = getItemDisplayState ? getItemDisplayState(item) : {
            status: item.status,
            excluded: item.excluded,
            loading: false
          };
          
          console.log(`📊 OrderDetails passing to OrderItem ${item.productNumber}:`, {
            itemId: item.id,
            realStatus: item.status,
            displayStatus: displayState.status,
            realExcluded: item.excluded,
            displayExcluded: displayState.excluded,
            loading: displayState.loading,
            hasGetItemDisplayState: !!getItemDisplayState
          });
          
          return (
            <OrderItem
              windowWidth={windowWidth}
              item={item}
              key={item.id}
              isLoading={displayState.loading}
              displayStatus={displayState.status}
              displayExcluded={displayState.excluded}
              hasZeroAmount={displayState.hasZeroAmount}
              isLast={index === stateOrderlines.length - 1}
              onAddToInventory={handleItemAction ? (included) => {
                console.log('🔗 OrderItem onAddToInventory called:', { itemId: item.id, included });
                handleItemAction(item.id, included);
              } : () => {}}
              onChangeAmount={(amount) => changeItemAmount(item.productNumber, amount)}
              onChangeExcluded={(excluded) => changeExcludedState(item.productNumber, excluded)}
              isSelected={
                !!selectedItems.find(
                  (productNumber) => productNumber === item.productNumber
                )
              }
              onRemoveItem={() => removeItems([item.productNumber])}
              onSelectItem={() => selectItems([item.productNumber])}
            />
          );
        })}
      </div>
    </div>
  );
};
